import { Button, Icon, Input, Select } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import styles from './styles.module.css';

interface WebhookHeader {
  id: string;
  type: 'bearer' | 'basic' | 'custom';
  bearer: { token: string };
  basic: { username: string; password: string };
  custom: { name: string; value: string };
}

function createEmptyHeader(id: string, type: 'bearer' | 'basic' | 'custom'): WebhookHeader {
  return {
    id,
    type,
    bearer: { token: '' },
    basic: { username: '', password: '' },
    custom: { name: '', value: '' },
  };
}

const HEADER_TYPE_OPTIONS = [
  { value: 'bearer', label: 'Bearer Authentication' },
  { value: 'basic', label: 'Basic Authentication' },
  { value: 'custom', label: 'Custom Header' },
] as const;

interface WebhookHeaderFieldProps {
  isEditing: boolean;
  headers: { [key: string]: string | number | boolean | object };
  onHeadersChange: (headers: { [key: string]: string }) => void;
}

export function WebhookHeaderField(props: WebhookHeaderFieldProps) {
  const { isEditing, headers, onHeadersChange } = props;

  const [entries, setEntries] = useState<WebhookHeader[]>(() => parseRawHeaders(headers as { [key: string]: string }));

  const hasAuthHeader = useMemo(
    () => entries.some((entry) => entry.type === 'bearer' || entry.type === 'basic'),
    [entries],
  );

  function handleEntriesChange(updatedEntries: WebhookHeader[]) {
    setEntries(updatedEntries);

    const convertedHeaders = userInputToRawHeaders(updatedEntries);
    onHeadersChange(convertedHeaders);
  }

  function updateEntry(updatedHeader: WebhookHeader) {
    handleEntriesChange(entries.map((entry): WebhookHeader => (entry.id === updatedHeader.id ? updatedHeader : entry)));
  }

  function removeEntry(id: string) {
    handleEntriesChange(entries.filter((entry) => entry.id !== id));
  }

  function addEntry() {
    handleEntriesChange([...entries, createEmptyHeader(crypto.randomUUID(), 'custom')]);
  }

  function getHeaderOptions(currentType: 'bearer' | 'basic' | 'custom') {
    const isCurrentEntryAuth = currentType === 'bearer' || currentType === 'basic';

    return HEADER_TYPE_OPTIONS.map((option) => ({
      ...option,
      disabled: (option.value === 'bearer' || option.value === 'basic') && hasAuthHeader && !isCurrentEntryAuth,
    }));
  }

  function handleTypeChange(id: string, newType: 'bearer' | 'basic' | 'custom') {
    handleEntriesChange(entries.map((entry) => (entry.id === id ? { ...entry, type: newType } : entry)));
  }

  if (isEditing) {
    return (
      <div className={classNames(styles.editorContainer, styles.editorContainerGapMedium)}>
        {entries.map((header) => {
          const headerOptions = getHeaderOptions(header.type);

          return (
            <div key={header.id} className={classNames(styles.fieldItem, styles.fieldItemColumn)}>
              <div className={styles.fieldRow}>
                <Select
                  options={headerOptions}
                  value={headerOptions.find((option) => header.type === option.value)}
                  onChange={(option) => {
                    if (isNil(option)) {
                      return;
                    }
                    handleTypeChange(header.id, option.value);
                  }}
                  className={styles.fieldDropdown}
                />

                {header.type === 'bearer' && (
                  <div className={styles.fieldInputWrapper}>
                    <Input
                      label="Bearer Token"
                      labelPosition="hidden"
                      name={`bearer-${header.id}`}
                      value={header.bearer.token}
                      onChange={(e) => updateEntry({ ...header, bearer: { token: e.value } })}
                      placeholder="Enter bearer token"
                    />
                  </div>
                )}

                {header.type === 'basic' && (
                  <>
                    <div className={styles.fieldInputWrapper}>
                      <Input
                        label="Username"
                        labelPosition="hidden"
                        name={`username-${header.id}`}
                        value={header.basic.username}
                        onChange={(e) => updateEntry({ ...header, basic: { ...header.basic, username: e.value } })}
                        placeholder="Username"
                      />
                    </div>
                    <div className={styles.fieldInputWrapper}>
                      <Input
                        label="Password"
                        labelPosition="hidden"
                        name={`password-${header.id}`}
                        value={header.basic.password}
                        onChange={(e) => updateEntry({ ...header, basic: { ...header.basic, password: e.value } })}
                        placeholder="Password"
                      />
                    </div>
                  </>
                )}

                {header.type === 'custom' && (
                  <>
                    <div className={styles.fieldInputWrapper}>
                      <Input
                        label="Header Name"
                        labelPosition="hidden"
                        name={`header-name-${header.id}`}
                        value={header.custom.name}
                        onChange={(e) => updateEntry({ ...header, custom: { ...header.custom, name: e.value } })}
                        placeholder="Header name"
                      />
                    </div>
                    <div className={styles.fieldInputWrapper}>
                      <Input
                        label="Header Value"
                        labelPosition="hidden"
                        name={`header-value-${header.id}`}
                        value={header.custom.value}
                        onChange={(e) => updateEntry({ ...header, custom: { ...header.custom, value: e.value } })}
                        placeholder="Header value"
                      />
                    </div>
                  </>
                )}

                <Button
                  kind="danger"
                  size="small"
                  iconStart="small-cross"
                  hideLabel
                  onClick={() => removeEntry(header.id)}
                  className={styles.fieldRemoveButton}>
                  Remove
                </Button>
              </div>

              <div className={styles.fieldPreview}>
                <div className={styles.fieldPreviewLabel}>Header preview</div>
                <div className={styles.fieldPreviewValue}>
                  {header.type === 'custom' && header.custom.name === '' && header.custom.value === '' ? (
                    <span className={styles.fieldPreviewPlaceholder}>Enter header name and value</span>
                  ) : (
                    formatHeaderPreview(header)
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <Button kind="neutral" size="small" iconStart="small-plus" onClick={addEntry} className={styles.addFieldButton}>
          Add Header
        </Button>
      </div>
    );
  }

  return (
    <div className={classNames(styles.editorContainer, styles.editorContainerGapMedium)}>
      {entries.map((entry) => (
        <div key={entry.id} className={styles.fieldItemTwoColumn}>
          <Icon name={entry.type === 'custom' ? 'small-document' : 'small-key'} kind="neutral" />

          <div className={styles.fieldContent}>
            <div className={styles.fieldTypeLabel}>
              {entry.type === 'bearer' ? 'Bearer Auth' : entry.type === 'basic' ? 'Basic Auth' : 'Custom'}
            </div>
            <div className={styles.monoText}>{formatHeaderPreview(entry)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatHeaderPreview(header: WebhookHeader): React.ReactNode {
  switch (header.type) {
    case 'bearer':
      return (
        <>
          <span className="bold-text">Authorization:</span> Bearer {header.bearer.token}
        </>
      );
    case 'basic':
      return (
        <>
          <span className="bold-text">Authorization:</span> Basic{' '}
          {Buffer.from(`${header.basic.username}:${header.basic.password}`, 'utf-8').toString('base64')}
        </>
      );
    case 'custom':
      return (
        <>
          <span className="bold-text">{header.custom.name}:</span> {header.custom.value}
        </>
      );
  }
}

function parseRawHeaders(headers: { [key: string]: string }): WebhookHeader[] {
  return Object.entries(headers).map(([headerName, headerValue]): WebhookHeader => {
    const baseHeader = createEmptyHeader(crypto.randomUUID(), 'custom');

    if (headerName.toLowerCase() === 'authorization') {
      if (headerValue.startsWith('Bearer ')) {
        const bearerToken = headerValue.split(' ')[1];
        return { ...baseHeader, type: 'bearer', bearer: { token: bearerToken } };
      }

      if (headerValue.startsWith('Basic ')) {
        const base64Token = headerValue.split(' ')[1];
        const decoded = Buffer.from(base64Token, 'base64').toString('utf-8');
        // split on first colon since password may contain colons
        const colonIndex = decoded.indexOf(':');
        const username = decoded.slice(0, colonIndex);
        const password = decoded.slice(colonIndex + 1);

        return { ...baseHeader, type: 'basic', basic: { username, password } };
      }
    }

    return { ...baseHeader, type: 'custom', custom: { name: headerName, value: headerValue } };
  });
}

function userInputToRawHeaders(headers: WebhookHeader[]) {
  return Object.fromEntries(
    headers.map((header) => {
      switch (header.type) {
        case 'bearer':
          return ['Authorization', `Bearer ${header.bearer.token.trim()}`];
        case 'basic':
          return [
            'Authorization',
            `Basic ${Buffer.from(`${header.basic.username.trim()}:${header.basic.password.trim()}`, 'utf-8').toString('base64')}`,
          ];
        case 'custom':
        default:
          return [header.custom.name.trim(), header.custom.value.trim()];
      }
    }),
  );
}
